import React, { useEffect, useRef, useState } from "react";
import { useScrollVisibility } from "../hooks/useScrollVisibility";

/**
 * ScrollIndicator component following SOLID principles (Single Responsibility).
 * Encapsulates UI and visibility logic for the scroll-down indicator orb.
 */
const ScrollIndicator = ({ threshold = 50 }) => {
  const isVisible = useScrollVisibility(threshold);
  const indicatorRef = useRef(null);
  const tapPulseIdRef = useRef(0);
  const [layoutState, setLayoutState] = useState({
    lineHeight: 160,
    collisionOpacity: 1,
    hasRoom: true,
  });
  const [tapPulses, setTapPulses] = useState([]);

  useEffect(() => {
    const indicatorEl = indicatorRef.current;
    const heroEl = indicatorEl?.closest(".hero");
    const statsEl = heroEl?.querySelector(".hero-stats");

    if (!indicatorEl || !statsEl) {
      return undefined;
    }

    let frameId = null;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const updateLayout = () => {
      frameId = null;

      const indicatorRect = indicatorEl.getBoundingClientRect();
      const statsRect = statsEl.getBoundingClientRect();

      const gap = indicatorRect.top - statsRect.bottom;
      const lineHeight = Math.max(0, Math.round(gap - 6));
      const MIN_VISIBLE_GAP = 32;
      const hasRoom = gap >= MIN_VISIBLE_GAP;

      // Keep indicator fully visible while there is comfortable spacing.
      // Fade only in the near-collision zone to avoid "early disappearing"
      // on certain orientations.
      const FULL_VISIBLE_GAP = 24;
      const HIDE_GAP = 4;
      const nearCollisionProgress = clamp(
        (gap - HIDE_GAP) / (FULL_VISIBLE_GAP - HIDE_GAP),
        0,
        1
      );
      const smoothstep =
        nearCollisionProgress *
        nearCollisionProgress *
        (3 - 2 * nearCollisionProgress);
      const collisionOpacity = Number(smoothstep.toFixed(3));

      setLayoutState((prev) => {
        if (
          prev.lineHeight === lineHeight &&
          Math.abs(prev.collisionOpacity - collisionOpacity) < 0.01 &&
          prev.hasRoom === hasRoom
        ) {
          return prev;
        }

        return { lineHeight, collisionOpacity, hasRoom };
      });
    };

    const requestLayoutUpdate = () => {
      if (frameId !== null) {
        return;
      }

      frameId = window.requestAnimationFrame(updateLayout);
    };

    const resizeObserver = new ResizeObserver(requestLayoutUpdate);
    resizeObserver.observe(indicatorEl);
    resizeObserver.observe(statsEl);
    resizeObserver.observe(heroEl);

    requestLayoutUpdate();

    window.addEventListener("resize", requestLayoutUpdate, { passive: true });
    window.visualViewport?.addEventListener("resize", requestLayoutUpdate, { passive: true });

    return () => {
      window.removeEventListener("resize", requestLayoutUpdate);
      window.visualViewport?.removeEventListener("resize", requestLayoutUpdate);
      resizeObserver.disconnect();

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  const triggerPressFeedback = () => {
    tapPulseIdRef.current += 1;
    setTapPulses((prev) => [...prev, tapPulseIdRef.current]);
  };

  const handleTapPulseEnd = (pulseId) => {
    setTapPulses((prev) => prev.filter((id) => id !== pulseId));
  };

  return (
    <div
      ref={indicatorRef}
      className={`hero-scroll-indicator ${!isVisible ? "hero-scroll-indicator--hidden" : ""} ${!layoutState.hasRoom ? "hero-scroll-indicator--collapsed" : ""}`}
      style={{
        "--hero-indicator-line-height": `${layoutState.lineHeight}px`,
        "--hero-indicator-collision-opacity": layoutState.collisionOpacity,
      }}
      >
        <button
          type="button"
          className="hero-scroll-indicator__visual"
          onPointerDown={triggerPressFeedback}
          aria-label="Индикатор прокрутки"
        >
          <div className="tech-core-container">
            <div className="tech-core-glow">
              <div className="tech-core-diamond" />
              <div className="tech-core-pulse tech-core-pulse--1" />
              <div className="tech-core-pulse tech-core-pulse--2" />
              {tapPulses.map((pulseId) => (
                <div
                  key={pulseId}
                  className="tech-core-pulse tech-core-pulse--tap"
                  onAnimationEnd={() => handleTapPulseEnd(pulseId)}
                />
              ))}
              <div className="tech-core-scanline" />
            </div>

            {/* Signal Tether - Vertical line connecting core to chevrons */}
            <div className="tech-signal-tether" />
          </div>

          <div className="tech-data-stream">
            <DataParticle className="tech-data-particle tech-data-particle--1" />
            <DataParticle className="tech-data-particle tech-data-particle--2" />
            <DataParticle className="tech-data-particle tech-data-particle--3" />
          </div>
        </button>
    </div>
  );
};

/* Traffic icon reused from tariff cards */
const DataParticle = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M12 2L2 7l10 5 10-5-10-5z" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M2 17l10 5 10-5" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M2 12l10 5 10-5" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

export default ScrollIndicator;
